import React from 'react';
import { Sparkles, FileText, Loader2 } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getTemplates, duplicateTemplate } from '../../services/overlayApi';
import { designsApi } from '../../services/designsApi';
import { useNavigate } from 'react-router-dom';
import TemplateCard from '../TemplateCard/TemplateCard';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
} from '../../components/ui/card';

const TemplateSelectionModal = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const { data: templates = [], isLoading } = useQuery({
    queryKey: ['templates'],
    queryFn: () => getTemplates(),
    enabled: isOpen,
    staleTime: 1000 * 60 * 5,
  });

  const createCreativeMutation = useMutation({
    mutationFn: designsApi.create,
    onSuccess: (data) => {
        // creative API returns the created object directly
      navigate(`/creative/builder/${data.id}`);
      onClose();
    },
    onError: (error) => {
      alert(`Failed to create design: ${error.message}`);
    }
  });

  const handleCreateFromScratch = () => {
      // Create a blank design
      createCreativeMutation.mutate({
          name: "Untitled Design",
          canvas_data: null, // Start empty
          product_ids: [],
          rules: { allow_edit: true }
      });
  };

  const handleSelectTemplate = (template) => {
      createCreativeMutation.mutate({
          name: template.name,
          canvas_data: template.design_data || template.canvas_data || null, // Copy template design data
          product_ids: [],
          rules: { allow_edit: true }
      });
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create new design</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6 mt-4">
          {/* Create from scratch option */}
          <Card
            className="cursor-pointer hover:border-neutral-400 transition-colors"
            onClick={handleCreateFromScratch}
          >
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-neutral-100 rounded-md">
                  <Sparkles className="size-5 text-neutral-700" />
                </div>
                <div>
                  <CardTitle className="text-base">Create from scratch</CardTitle>
                  <CardDescription>Start with a blank canvas</CardDescription>
                </div>
              </div>
            </CardHeader>
          </Card>

          {/* Templates section */}
          <div>
            <h3 className="font-medium text-neutral-900 mb-4">Use a template</h3>
            
            {isLoading ? (
              <div className="flex items-center justify-center py-10 text-neutral-500 gap-2">
                <Loader2 className="size-5 animate-spin" />
                <span className="text-sm">Loading templates...</span>
              </div>
            ) : (
              <div className="grid grid-cols-4 gap-4">
                {templates?.templates?.slice(0, 8).map((template) => (
                  <TemplateCard
                    key={template.id}
                    template={template}
                    variant="simple"
                    onClick={() => handleSelectTemplate(template)}
                    // No onDelete prop needed here as we are selecting
                  />
                ))}
                
                {templates?.templates?.length === 0 && (
                  <div className="col-span-2 text-center py-8 text-neutral-500 text-sm">
                    No templates available yet.
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
export default TemplateSelectionModal;
